'use client';

import { ChevronLeftIcon, ChevronRightIcon } from 'tdesign-icons-react';
import { animated } from '@react-spring/web';
import { StaticImageData } from '../image';
import { LinkButton } from '../button';
import { useSpringCarousel, useMedia } from '@ssv-website/hooks';
import { RefObject, useEffect } from 'react';
import useScrollPage from '../page/useScrollPage';
import { DotPage } from '../page/DotPage';

interface IArrowIndicatorProps {
  index: number;
  total: number;

  onPrev(): void;

  onNext(): void;

  onDot(value: number): void;
}

interface ICarouselItemProps {
  title: string;
  subTitle: string;
  linkText: string;
  linkHref: string;
  imageUrl?: string | StaticImageData;
  videoUrl?: string;
  mobileImageUrl?: string | StaticImageData;
  mobileVideoUrl?: string;
  mobileTitle?: string;
  type?: 1 | 2;
  themeStyle?: {
    titleColor?: string;
    blurBgColor?: string;
  };
}

interface ICarouselProps {
  data: Array<ICarouselItemProps>;
}

const range = (value: number) =>
  Array.from({ length: value })
    .fill(null)
    .map((_, i) => i + 1);

const ArrowIndicator: React.FC<IArrowIndicatorProps> = ({
  index = 0,
  total = 0,
  onDot,
  onNext,
  onPrev,
}: IArrowIndicatorProps) => {
  return (
    <div className="flex gap-x-8">
      <ChevronLeftIcon
        name="chevron-left"
        color="rgba(53,63,73,.6)"
        className="cursor-pointer w-[14px] h-[14px]"
        onClick={onPrev}
      />
      <div className="flex gap-x-6 items-center">
        {range(total).map((m, i) => (
          <div
            key={m}
            className="bg-primary w-2 h-2 rounded-full cursor-pointer"
            style={{ opacity: i === index ? 1 : 0.1 }}
            onClick={() => onDot(i)}
          />
        ))}
      </div>
      <ChevronRightIcon
        name="chevron-right"
        color="rgba(53,63,73,.6)"
        className="cursor-pointer w-[14px] h-[14px] disabled"
        onClick={onNext}
      />
    </div>
  );
};

export const CarouselItem: React.FC<ICarouselItemProps> = ({
  title,
  subTitle,
  linkText,
  linkHref,
  themeStyle,
  mobileTitle,
}) => {
  const isMobile = useMedia('mobile');
  const _title = isMobile ? mobileTitle || title : title;
  return (
    <div className="flex flex-col mobile:justify-between mobile:p-5 mobile:h-full">
      <div className="flex-1">
        <h3
          className="text-[28px] mt-5 leading-[40px] text-active mix-blend-difference font-500 mobile:mt-0 mobile:text-lg mobile:leading-[26px] text-nowrap"
          style={{ color: themeStyle?.titleColor || '#0a65e0' }}
        >
          {_title}
        </h3>
        <p
          className="text-base -leading-[1px]  mt-[2px] text-active mix-blend-difference mobile:text-xs mobile:mt-[6px]"
          style={{
            color: themeStyle?.titleColor || '#0a65e0',
          }}
        >
          {subTitle}
        </p>
      </div>
      <div>
      {linkHref && (
        <LinkButton
          linkHref={linkHref}
          className="bg-white mb-5 mobile:mb-0"
          color={themeStyle?.titleColor}
          arrowIcon={false}
          linkClassName='inline-block'
        >
          {linkText}
        </LinkButton>
      )}
      </div>
    </div>
  );
};

const getImg = (img: any) => {
  if (typeof img === 'object') {
    return img.src;
  }
  return img;
};

export const Carousel: React.FC<ICarouselProps> = ({ data = [] }) => {
  const isMobile = useMedia('mobile');
  const {
    transitions,
    active,
    onPrev,
    onDot,
    onNext,
    inViewRef,
    onStop,
    onStart,
  } = useSpringCarousel({
    value: 0,
    duration: 7000,
    total: data.length,
    stopAuto: isMobile,
    disabled: false,
  });

  const { ref, bind, current } = useScrollPage({
    total: data.length,
    gap: 0,
    current: active,
  });

  const refObj = isMobile? { ref, ...bind() }: {}

  useEffect(() => {
    onDot(current);
  }, [current]);
  return (
    <div
      ref={inViewRef as RefObject<any>}
      className="relative w-full flex flex-col justify-center"
    >
      <div className="overflow-hidden touch-pan-y" {...refObj}>
        <animated.div className="relative flex h-[200px] mobile:h-auto mobile:aspect-[335/240]">
          {transitions(({ opacity, transform }, i) => (
            <animated.div
              className="max-w-5xl w-full overflow-hidden rounded-2xl absolute left-0 top-0 mobile:h-auto mobile:rounded-lg"
              key={i}
              onMouseEnter={onStop}
              onMouseLeave={onStart}
              style={{ opacity, transform }}
            >
              <div className="relative h-[200px] rounded-2xl mobile:rounded-lg mobile:h-auto mobile:aspect-[335/240]">
                <div
                  className="bg-no-repeat h-full bg-cover rounded-2xl bg-right mobile:rounded-lg"
                  style={{
                    backgroundImage: `url(${getImg(
                      isMobile
                        ? data[i]?.mobileImageUrl || data[i]?.imageUrl
                        : (data[i]?.imageUrl as any)
                    )})`,
                  }}
                ></div>
                {data[i]?.themeStyle?.blurBgColor ? (
                  <div
                    className="absolute left-0 top-0 w-full h-full bg-no-repeat"
                    style={{
                      backgroundSize: '600px',
                      backgroundImage: isMobile? undefined: `linear-gradient(to right, ${
                        data[i]?.themeStyle?.blurBgColor || '#fff'
                      } 0%, transparent 100%)`,
                    }}
                  ></div>
                ) : null}

                <div className="absolute left-10 top-1/2 -translate-y-1/2 mobile:left-1/2 mobile:-translate-x-1/2 mobile:h-full mobile:w-full">
                  <CarouselItem
                    title={data[i]?.title}
                    subTitle={data[i]?.subTitle}
                    linkHref={data[i]?.linkHref}
                    linkText={data[i]?.linkText}
                    themeStyle={data[i]?.themeStyle}
                    mobileTitle={data[i]?.mobileTitle}
                  />
                </div>
              </div>
            </animated.div>
          ))}
        </animated.div>
      </div>
      {data.length > 1 && (
        <div className="flex justify-center mt-10 mobile:mt-5">
          <div className="block mobile:hidden">
            <ArrowIndicator
              index={active}
              total={data.length}
              onPrev={onPrev}
              onDot={onDot}
              onNext={onNext}
            />
          </div>
          <div className="hidden mobile:block">
            <DotPage total={data.length} current={active} />
          </div>
        </div>
      )}
    </div>
  );
};
